
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ExternalLink, BarChart2, FileText, Clock, CheckCircle, TrendingUp } from "lucide-react";
import { getResponsiveImageProps } from "@/utils/imageOptimizer";

const Valocap = () => {
  const features = [
    {
      icon: BarChart2,
      title: "Suivi en temps réel", 
      description: "Visualisez vos données de collecte et valorisation à tout moment, avec des indicateurs clairs et précis."
    },
    {
      icon: FileText,
      title: "Rapports personnalisés",
      description: "Générez des rapports détaillés adaptés à vos besoins pour un suivi optimal de vos performances environnementales."
    },
    {
      icon: Clock,
      title: "Historique complet",
      description: "Accédez à l'historique de vos données sur plusieurs années pour analyser et optimiser votre gestion des déchets."
    }
  ];

  const benefits = [
    "Suivi personnalisé de vos déchets",
    "Taux de valorisation des déchets en temps réel", 
    "Calcul des économies de CO2 réalisées",
    "Génération de rapports de gestion environnementale",
    "Tableau de bord personnalisable",
    "Historique complet de vos données"
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section modernisé */}
        <section className="relative bg-gradient-to-br from-fabre-green via-fabre-teal to-fabre-darkgreen text-white py-20 md:py-28 overflow-hidden">
          {/* Éléments décoratifs */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
            <div className="absolute top-32 right-20 w-24 h-24 border border-white/20 rounded-full"></div>
            <div className="absolute bottom-20 left-1/4 w-16 h-16 border border-white/20 rounded-full"></div>
            <div className="absolute bottom-32 right-1/3 w-20 h-20 border border-white/20 rounded-full"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {/* Icône d'en-tête */}
              <div className="flex justify-center mb-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-full p-4">
                  <TrendingUp className="w-12 h-12 text-white" />
                </div>
              </div>

              <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6 leading-tight">
                VALOCAP
                <span className="block text-3xl md:text-4xl mt-2 text-fabre-lightgreen">
                  Votre plateforme digitale
                </span>
              </h1>
              
              <div className="w-24 h-1.5 bg-white/30 mx-auto mb-8 rounded-full"></div>
              
              <p className="text-xl md:text-2xl opacity-90 leading-relaxed mb-12 max-w-3xl mx-auto">
                Une solution digitale complète pour suivre et optimiser votre gestion des déchets en temps réel
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="bg-white text-fabre-green hover:bg-gray-100 px-8 py-4 text-lg font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  onClick={() => window.open('https://www.valocap.fr/', '_blank')}
                >
                  Accéder à Valocap
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Vague décorative */}
          <div className="absolute bottom-0 left-0 w-full overflow-hidden">
            <svg 
              className="relative block w-full h-[50px]" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 1440 100" 
              preserveAspectRatio="none"
            >
              <path 
                fill="#ffffff" 
                fillOpacity="1" 
                d="M0,32L80,42.7C160,53,320,75,480,74.7C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,100L1360,100C1280,100,1120,100,960,100C800,100,640,100,480,100C320,100,160,100,80,100L0,100Z"
              ></path>
            </svg>
          </div>
        </section>

        {/* Section de présentation améliorée */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 text-fabre-gray">
                    La plateforme de gestion de vos déchets
                  </h2>
                  <div className="w-24 h-1.5 bg-gradient-to-r from-fabre-green to-fabre-teal mb-8 rounded-full"></div>
                </div>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Valocap est votre outil digital qui vous donne un accès complet à vos données de gestion des déchets avec une interface moderne et intuitive.
                </p>

                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                  <h3 className="text-xl font-bold text-fabre-gray mb-6 flex items-center">
                    <CheckCircle className="h-6 w-6 text-fabre-green mr-3" />
                    Fonctionnalités principales
                  </h3>
                  <ul className="space-y-4">
                    {benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-fabre-green mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4">
                  <Button 
                    size="lg"
                    className="bg-fabre-green hover:bg-fabre-darkgreen px-8 py-4 text-lg font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                    onClick={() => window.open('https://www.valocap.fr/', '_blank')}
                  >
                    Découvrir la plateforme
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-fabre-green/20 to-fabre-teal/20 rounded-3xl transform rotate-3"></div>
                <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200 transform -rotate-1 hover:rotate-0 transition-transform duration-300">
                  <img
                    src="/lovable-uploads/3f73765a-c2b3-4a12-a805-3b512f7b174d.png"
                    alt="Interface Valocap - Tableau de bord de gestion des déchets"
                    className="w-full h-auto"
                    {...getResponsiveImageProps("/lovable-uploads/3f73765a-c2b3-4a12-a805-3b512f7b174d.png")}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section fonctionnalités modernisée */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 text-fabre-gray">
                Les fonctionnalités avancées
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-fabre-green to-fabre-teal mx-auto mb-8 rounded-full"></div>
              <p className="text-xl text-gray-600 leading-relaxed">
                Une suite complète d'outils pour optimiser votre gestion environnementale
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="group">
                  <div className="bg-gradient-to-br from-white to-fabre-lightgray p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-fabre-green/30 transform hover:-translate-y-2">
                    <div className="mb-6 p-4 bg-gradient-to-br from-fabre-lightgreen to-fabre-green/20 rounded-2xl inline-block group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="h-8 w-8 text-fabre-green" />
                    </div>
                    <h3 className="font-heading font-bold text-xl mb-4 text-fabre-gray group-hover:text-fabre-green transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section CTA finale */}
        <section className="py-20 bg-gradient-to-br from-fabre-lightgray to-white relative overflow-hidden">
          {/* Éléments décoratifs */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 left-20 w-40 h-40 border border-fabre-green rounded-full"></div>
            <div className="absolute bottom-20 right-20 w-32 h-32 border border-fabre-teal rounded-full"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 text-fabre-gray">
                Prêt à optimiser votre gestion des déchets ?
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-fabre-green to-fabre-teal mx-auto mb-8 rounded-full"></div>
              <p className="text-xl text-gray-600 mb-12 leading-relaxed">
                Découvrez comment Valocap peut transformer votre approche de la gestion environnementale
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button 
                  size="lg"
                  className="bg-fabre-green hover:bg-fabre-darkgreen px-10 py-4 text-lg font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  onClick={() => window.open('https://www.valocap.fr/', '_blank')}
                >
                  Accéder à Valocap
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-2 border-fabre-green text-fabre-green hover:bg-fabre-green hover:text-white px-10 py-4 text-lg font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  onClick={() => window.location.href = '/contact'}
                >
                  Nous contacter
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Valocap;
